import { useCallback } from "react";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleRecognitionOverrides } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ShowLockedPeriods,
    useScheduleLockedPeriodsDialog,
} from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useApplyScheduleWithLinkedConflict } from "@components/accounting/schedules/linkedConflict/useApplyScheduleWithLinkedConflict";

export interface BillScheduleApplyTarget {
    entityId: number;
    billId: string;
    amount: number;
    /** Set for a line-scoped apply; omitted applies to the whole bill. */
    sourceLineIds?: string[];
    specificDate?: string;
    termStart?: string;
    overrides?: ScheduleRecognitionOverrides;
}

export function buildBillSchedulePayload(
    target: BillScheduleApplyTarget,
): ApplyScheduleTemplateBody {
    return {
        sourceType: ScheduleSourceType.bill,
        entityId: target.entityId,
        billId: target.billId,
        amount: Math.abs(target.amount),
        ...(target.sourceLineIds
            ? { sourceLineIds: target.sourceLineIds }
            : {}),
        ...(target.specificDate ? { specificDate: target.specificDate } : {}),
        ...(target.termStart ? { termStart: target.termStart } : {}),
        ...(target.overrides ? { overrides: target.overrides } : {}),
    };
}

/** Single apply call site for the bill Schedule cells and their schedule modal. */
export function useApplyBillSchedule({
    showLockedPeriods: showLocalLockedPeriods,
}: { showLockedPeriods?: ShowLockedPeriods } = {}) {
    const { toast, error } = useToaster();
    const { applyWithLinkedConflict, linkedConflictModal, isApplying } =
        useApplyScheduleWithLinkedConflict({
            showLockedPeriods: showLocalLockedPeriods,
        });
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        showLocalLockedPeriods,
    );

    const applySchedule = useCallback(
        async (options: {
            templateId: string;
            target: BillScheduleApplyTarget;
            onApplied?: () => Promise<void> | void;
        }): Promise<boolean> => {
            try {
                const result = await applyWithLinkedConflict({
                    templateId: options.templateId,
                    payload: buildBillSchedulePayload(options.target),
                    onApplied: options.onApplied,
                });
                if (result.outcome === "awaitingChoice") {
                    return true;
                }
                toast("Schedule applied");
                await options.onApplied?.();
                return true;
            } catch (applyError) {
                if (!showLockedPeriods(applyError)) {
                    error(
                        applyError instanceof Error
                            ? applyError.message
                            : "Failed to apply schedule",
                    );
                }
                return false;
            }
        },
        [applyWithLinkedConflict, error, showLockedPeriods, toast],
    );

    return { applySchedule, isApplying, linkedConflictModal };
}
